import { BookService } from './bookService';
import { mentorChatService, MentorGatewayMessage } from './mentorChatService';
import { OnboardingPhase1Schema } from '../utils/mentorSchemas';

export type OnboardingQA = {
  question: string;
  answer: string | number | boolean | string[] | null | undefined;
};

const AUDIENCE_EXPANSION_MAP: Record<string, string[]> = {
  parents: ['parents'],
  students: ['students'],
  entrepreneurs: ['entrepreneurs'],
  startup_founders: ['startup_founders'],
  managers: ['managers'],
  leaders: ['leaders'],
};

const toText = (value: OnboardingQA['answer']): string => {
  if (Array.isArray(value)) {
    return value.map(item => String(item)).join(', ');
  }
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
};

export const deriveAudiencesFromAnswers = (answers: OnboardingQA[]): string[] => {
  const audiences: string[] = [];

  const genderAnswer = answers.find(qa => qa.question.toLowerCase().includes('identify'));
  if (genderAnswer?.answer) {
    const gender = toText(genderAnswer.answer).toLowerCase();
    if (gender === 'male') {
      audiences.push('men');
    } else if (gender === 'female') {
      audiences.push('women');
    }
  }

  const ageAnswer = answers.find(qa => qa.question.toLowerCase().includes('age'));
  if (ageAnswer?.answer) {
    const age = toText(ageAnswer.answer);
    if (age === '18-24') {
      audiences.push('teens');
    }
  }

  const audienceAnswer = answers.find(qa => qa.question.toLowerCase().includes('who are you'));
  if (audienceAnswer?.answer) {
    const raw = Array.isArray(audienceAnswer.answer)
      ? audienceAnswer.answer.map(ans => String(ans))
      : [String(audienceAnswer.answer)];
    const expanded = raw.flatMap(answer => AUDIENCE_EXPANSION_MAP[answer] || []);
    audiences.push(...expanded);
  }

  return Array.from(new Set(audiences));
};

export const buildPhase1Messages = (
  answers: OnboardingQA[],
  calculationResults?: any
): MentorGatewayMessage[] => {
  const normalize = (qa: OnboardingQA | undefined) => (qa ? toText(qa.answer) : 'Not specified');

  const goalAnswer = answers.find((qa) => {
    const q = qa.question.toLowerCase();
    return (q.includes('goal') || q.includes('outcome')) && (q.includes('main') || q.includes('right now'));
  });

  const challengeAnswer = answers.find((qa) => {
    const q = qa.question.toLowerCase();
    return (q.includes('holding') && q.includes('back')) || q.includes('biggest thing') || q.includes('challenge');
  });

  const otherContext = answers
    .filter((qa) => qa !== goalAnswer && qa !== challengeAnswer)
    .map((qa) => `${qa.question}: ${toText(qa.answer)}`)
    .join('\n');

  const calculationText = calculationResults
    ? `\n\nCalculation Results:\n${JSON.stringify(calculationResults, null, 2)}`
    : '';

  const content = `I just completed onboarding. Here is my profile:

PRIMARY GOAL:
${goalAnswer ? `${goalAnswer.question}: ${normalize(goalAnswer)}` : 'Not specified'}

MAIN CHALLENGE:
${challengeAnswer ? `${challengeAnswer.question}: ${normalize(challengeAnswer)}` : 'Not specified'}

OTHER CONTEXT:
${otherContext}

${calculationText ? `${calculationText}\n` : ''}Analyze my answers and extract:
1. Main focus category (must match my stated goal)
2. Goals - what I want to achieve (from my answers)
3. Interests - topics I'm interested in
4. Challenges - problems or obstacles I face
5. Personality traits - key characteristics you observe
6. A brief welcome message (1-2 sentences)
7. A SIMPLE search query (2-4 key terms for finding books/insights)

Be specific and extract information directly from my responses.
Keep the search query SHORT - just key terms like "leadership founders" or "parenting sleep".`;

  return [
    {
      role: 'user',
      content,
    },
  ];
};

export const getVectorRecommendations = async (
  searchQuery: string,
  audiences: string[] = [],
  language: string = 'en'
) => {
  const searchResult = await BookService.vectorSearch(
    searchQuery,
    ['book', 'insight'],
    language,
    10,
    5,
    audiences.length
      ? {
          audiences,
          audienceFilterMode: 'strict',
        }
      : undefined
  );

  if (!searchResult.success || !searchResult.data) {
    throw new Error(searchResult.error || 'Vector search failed');
  }

  const { books, insights } = searchResult.data;
  const topBooks = books.slice(0, 5);
  const topInsights = insights.slice(0, 5);

  return {
    personalizedMessage: `Based on your goals, I've selected ${topBooks.length} books and ${topInsights.length} insights to get you started.`,
    recommendedBooks: topBooks.map((book: any) => ({
      bookId: String(book.id),
      reason: '',
    })),
    insightPlan: {
      title: 'Your First Steps',
      insights: topInsights.map((insight: any) => ({
        insightId: String(insight.id),
        reason: '',
        bookId: insight.bookId ? String(insight.bookId) : undefined,
      })),
    },
    query: searchQuery,
  };
};

export const runPhase1Extraction = async (answers: OnboardingQA[], calculationResults?: any) => {
  const messages = buildPhase1Messages(answers, calculationResults);
  return mentorChatService.sendStructuredRequest(
    messages,
    OnboardingPhase1Schema,
    { responseStyle: 'short', modelPreference: 'standard' }
  );
};

